import { getAuth } from 'firebase/auth'
import * as Sentry from '@sentry/browser'
import { z } from 'zod'
import { TableChat, TableChatSchema } from '../chats'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { readResponseJson, getResponseDebugContext } from '@/util/api/response-body'
import { ApiError } from '@/util/errors/api-error'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'

/** List accessible chats associated with this table. */
export async function getTableChats(tableId: string, signal?: AbortSignal): Promise<TableChat[]> {
  const token = await getAuth().currentUser?.getIdToken()
  if (!token) throw new Error('Sign in to access tables.')
  const url = `${kPaxtonAppApiRESTBaseUrl()}/api/v1/tables/${tableId}/chats`
  const response = await fetch(url, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    signal,
  })
  const { data, parseError } = await readResponseJson(response)
  signal?.throwIfAborted()
  if (!response.ok) {
    const detail = data && typeof data === 'object' && 'detail' in data ? data.detail : undefined
    if (response.status >= 500) Sentry.captureException(new Error('Table request failed'), { extra: getResponseDebugContext(response, url) })
    throw new ApiError(
      response.status,
      response.status >= 400 && response.status < 500 && typeof detail === 'string' ? detail : 'Unable to load table chats.',
      response.status < 500 ? detail : undefined
    )
  }
  const parsed = z.array(TableChatSchema).safeParse(data)
  if (parseError || !parsed.success) {
    Sentry.captureException(new Error('Invalid table response'), { extra: getResponseDebugContext(response, url) })
    throw new ApiResponsePayloadError(response.status, 'The table response could not be read. Please retry.')
  }
  return parsed.data
}
